import { motion } from "framer-motion";
import { useState } from "react";

import * as uf from "../../../../common/unfathomable";
import { art } from "./assets";
import { useTooltip } from "./CardFace";
import { kHostTip } from "./Hud";
import { Rich } from "./RichText";
import css from "./Unfathomable.module.css";
import type { UnfathomableConnection } from "./useUnfathomableRoom";
import { savedName } from "./useUnfathomableRoom";

const kCharacterIds = Object.keys(uf.kCharacters) as uf.CharacterId[];

export function Title() {
  return (
    <div className={css.lobbyHeader}>
      <motion.h1 className={css.title} initial={{ opacity: 0, y: -20, letterSpacing: "0.3em" }} animate={{ opacity: 1, y: 0, letterSpacing: "0.06em" }} transition={{ duration: 1.2 }}>
        Unfathomable
      </motion.h1>
      <div className={css.subtitle} style={{ fontSize: "1.4rem" }}>深 渊 之 中</div>
      <p>1913 年，SS 亚特兰蒂卡号。船上潜伏着与深潜者血脉相连的叛徒……</p>
    </div>
  );
}

export function RoomPicker({ conn }: { conn: UnfathomableConnection }) {
  const [name, setName] = useState(savedName());
  return (
    <div className={css.lobby}>
      <Title />
      <div className={`${css.frame}`} style={{ padding: 16, marginBottom: 20, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <label>你的名字：<input className={css.input} value={name} maxLength={20} onChange={e => setName(e.target.value)} placeholder="船员姓名" /></label>
        <button className={`${css.btn}`} onClick={() => { conn.refreshRooms(); }}>刷新房间</button>
      </div>
      <div className={css.roomList}>
        {conn.rooms.map(r => (
          <motion.div key={r.roomId} className={`${css.roomCard} ${css.darkFrame}`} whileHover={{ y: -4 }}>
            <div className={css.subtitle} style={{ fontSize: "1.3rem" }}>{r.displayName}</div>
            <div style={{ margin: "8px 0", color: "var(--parchment-3)" }}>在线 {r.clients} 人</div>
            <button className={css.btn} disabled={!name.trim()} onClick={() => conn.join(r.roomId, name.trim(), r.displayName)}>登船</button>
          </motion.div>
        ))}
        {conn.rooms.length === 0 && <div className={css.waiting}>没有可用的房间（服务器是否已启动？）</div>}
      </div>
    </div>
  );
}

function CharacterChoice(props: { id: uf.CharacterId; taken: boolean; mine: boolean; artOk: boolean; onPick: () => void }) {
  const c = uf.kCharacters[props.id];
  const tip = useTooltip(
    <div>
      <b>{c.name}（{c.title}）</b>
      <div>起始：{uf.spaceName(c.startSpace)} · {uf.kItemById[c.startItem].name}</div>
      <div style={{ marginTop: 4 }}><b>{c.ability.name}</b>：<Rich text={c.ability.text} /></div>
      <div style={{ marginTop: 4 }}><b>叛变能力（公开身份后可用）：{c.reveal.name}</b>：<Rich text={c.reveal.text} /></div>
      <div style={{ marginTop: 4 }}><b>特技：{c.feat.name}</b>：<Rich text={c.feat.text} /></div>
    </div>,
  );
  const cls = [css.charChoice, css.darkFrame, props.taken && !props.mine ? css.charChoiceTaken : "", props.mine ? css.charChoiceMine : ""].join(" ");
  return (
    <motion.div className={cls} onClick={props.taken && !props.mine ? undefined : props.onPick} whileTap={{ scale: 0.96 }} {...tip}>
      {props.artOk ? <img className={css.portrait} src={art.token(props.id)} alt={c.name} /> : <div className={css.portrait}>{c.name[0]}</div>}
      <div style={{ fontWeight: 700 }}>{c.name}</div>
      <div style={{ fontSize: "0.8rem", color: "var(--parchment-3)" }}>{c.title}</div>
      <div className={css.skillPips}>
        {(Object.entries(c.skills) as [uf.SkillType, number][]).map(([t, n]) => (
          <span key={t} className={`${css.pip} ${css[`pip_${t}`]}`}>{uf.kSkillNameZh[t]}×{n}</span>
        ))}
      </div>
    </motion.div>
  );
}

export function RoomLobby({ conn, lobby, artOk }: { conn: UnfathomableConnection; lobby: uf.LobbyView; artOk: boolean }) {
  const mySlot = lobby.yourSlot;
  const me = mySlot !== null ? lobby.seats[mySlot] : null;
  const [name, setName] = useState(me?.name ?? savedName());
  const isHost = mySlot !== null && lobby.hostSlot === mySlot;
  const taken = new Set(lobby.seats.map(s => s.character).filter(Boolean));
  const seated = lobby.seats.filter(s => s.name).length;

  const sit = (slot: number) => conn.send(uf.Api.kTakeSeat, { slot, name: name || "玩家", character: null } as uf.TakeSeatRequest);
  const pick = (ch: uf.CharacterId) => {
    if (mySlot === null) return;
    conn.send(uf.Api.kTakeSeat, { slot: mySlot, name: name || "玩家", character: me?.character === ch ? null : ch } as uf.TakeSeatRequest);
  };
  const setOpt = (o: Partial<uf.RoomOptions>) => conn.send(uf.Api.kConfigure, { options: o } as uf.ConfigureRequest);

  const onRestore = (file: File) => {
    file.text().then(t => conn.send(uf.Api.kRestore, { backup: JSON.parse(t) })).catch(() => alert("存档文件无法读取"));
  };

  return (
    <div className={css.lobby}>
      <Title />
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <div className={css.subtitle} style={{ fontSize: "1.4rem" }}>{lobby.roomName}</div>
        <div style={{ flex: 1 }} />
        <label>名字：<input className={css.input} value={name} maxLength={20} onChange={e => setName(e.target.value)}
          onBlur={() => mySlot !== null && conn.send(uf.Api.kTakeSeat, { slot: mySlot, name, character: me?.character ?? null })} /></label>
        <button className={`${css.btn} ${css.btnGhost}`} onClick={conn.leave}>离开房间</button>
      </div>

      <div className={css.rule} />
      <div className={css.subtitle}>船员席位（{seated}/6，需要 3–6 人）</div>
      <div style={{ fontSize: "0.8rem", color: "var(--parchment-3)", marginBottom: 6 }}>
        👑 房主：{lobby.hostSlot !== null ? `${lobby.seats.find(s => s.slot === lobby.hostSlot)?.name}${isHost ? "（你）" : ""}` : "暂无"}——座位最靠前、当前在线的真人玩家，负责开局、设置、机器人、恢复存档与结束本局；掉线时自动交给下一位
      </div>
      <div className={css.seatGrid}>
        {lobby.seats.map(s => (
          <div key={s.slot} className={`${css.seat} ${css.darkFrame} ${s.name ? "" : css.seatEmpty}`}>
            {s.character ? (artOk ? <img className={css.portrait} src={art.token(s.character)} alt="" /> : <div className={css.portrait}>{uf.kCharacters[s.character].name[0]}</div>)
              : <div className={css.portrait}>?</div>}
            <div style={{ fontWeight: 700 }}>{s.bot ? "🤖 " : ""}{s.name ?? "空位"}{s.slot === mySlot ? "（你）" : ""}{s.slot === lobby.hostSlot && <span title={kHostTip}> 👑</span>}</div>
            <div style={{ fontSize: "0.8rem", color: "var(--parchment-3)" }}>{s.character ? uf.kCharacters[s.character].name : s.name ? "尚未选择角色" : ""}{s.name && !s.connected ? "（离线）" : ""}</div>
            {!s.name && <button className={`${css.btn} ${css.btnSmall}`} onClick={() => sit(s.slot)}>入座</button>}
            {!s.name && isHost && lobby.options.testMode && (
              <button className={`${css.btn} ${css.btnSmall} ${css.btnGhost}`} onClick={() => conn.send("addBot", { slot: s.slot })}>🤖 添加机器人</button>
            )}
            {s.bot && isHost && <button className={`${css.btn} ${css.btnSmall} ${css.btnGhost}`} onClick={() => conn.send("removeBot", { slot: s.slot })}>移除机器人</button>}
            {s.slot === mySlot && <button className={`${css.btn} ${css.btnSmall} ${css.btnGhost}`} onClick={() => conn.send("leaveSeat")}>离座</button>}
          </div>
        ))}
      </div>

      {mySlot !== null && (
        <>
          <div className={css.subtitle}>选择你的角色</div>
          <div className={css.charGrid} style={{ margin: "10px 0 20px" }}>
            {kCharacterIds.map(id => (
              <CharacterChoice key={id} id={id} artOk={artOk} taken={taken.has(id)} mine={me?.character === id} onPick={() => pick(id)} />
            ))}
          </div>
        </>
      )}

      <div className={`${css.frame}`} style={{ padding: 16, display: "grid", gap: 10 }}>
        <div className={css.subtitle} style={{ color: "var(--brass-lo)" }}>航行设置{isHost ? "" : "（仅房主可修改）"}</div>
        <label><input type="checkbox" disabled={!isHost} checked={lobby.options.noCultist} onChange={e => setOpt({ noCultist: e.target.checked })} /> 无邪教徒变体（4/6 人局，资源 7/6/6/7）</label>
        <label>难度：<select className={css.input} disabled={!isHost} value={lobby.options.difficulty} onChange={e => setOpt({ difficulty: Number(e.target.value) })}>
          <option value={2}>简单（资源 +2）</option><option value={0}>标准</option><option value={-2}>困难（资源 −2）</option>
        </select></label>
        <label><input type="checkbox" disabled={!isHost} checked={lobby.options.maskWindows} onChange={e => setOpt({ maskWindows: e.target.checked })} /> 响应窗口伪装（防止停顿暴露谁持有响应牌）</label>
        <label>响应窗口最短时长：<input className={css.input} type="number" min={0} max={10000} step={100} disabled={!isHost} value={lobby.options.windowMinMs}
          onChange={e => setOpt({ windowMinMs: Number(e.target.value) })} style={{ width: 90 }} /> 毫秒</label>
        <div className={css.rule} />
        <label><input type="checkbox" disabled={!isHost} checked={lobby.options.testMode} onChange={e => setOpt({ testMode: e.target.checked })} /> 🧪 测试模式：允许机器人入座；任何人都能下载包含全部隐藏信息的完整对局记录</label>
        {lobby.options.testMode && (
          <>
            <label>固定随机种子：<input className={css.input} type="number" min={0} disabled={!isHost} placeholder="留空 = 随机"
              value={lobby.options.seed ?? ""} onChange={e => setOpt({ seed: e.target.value === "" ? null : Number(e.target.value) })} style={{ width: 140 }} /></label>
            <label>机器人思考时间：<input className={css.input} type="number" min={0} max={10000} step={100} disabled={!isHost}
              value={lobby.options.botDelayMs} onChange={e => setOpt({ botDelayMs: Number(e.target.value) })} style={{ width: 90 }} /> 毫秒</label>
          </>
        )}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button className={css.btn} disabled={!isHost || seated < 3 || lobby.seats.some(s => s.name && !s.character)} onClick={() => conn.send(uf.Api.kStart)}>⚓ 起航</button>
          {isHost && (
            <label className={`${css.btn} ${css.btnGhost}`} style={{ cursor: "pointer" }}>
              读取存档…<input type="file" accept="application/json" style={{ display: "none" }} onChange={e => e.target.files?.[0] && onRestore(e.target.files[0])} />
            </label>
          )}
          <span className={css.waiting} style={{ color: "var(--brass-lo)" }}>起始玩家将随机决定。</span>
        </div>
      </div>
    </div>
  );
}
